import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Boat, ApiError } from '../../lib/api.ts';
import { TrackHistory } from '../../components/TrackHistory.tsx';

export function AdminBoats() {
  const qc = useQueryClient();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Boat | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [openPoints, setOpenPoints] = useState<string | null>(null);

  const boats = useQuery({
    queryKey: ['boats', includeInactive],
    queryFn: () => api.listBoats(includeInactive),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.updateBoat(id, { active: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boats'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Båtregister</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Ny båt
        </button>
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-400 mb-3">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Vis arkiverte
      </label>

      {(showNew || editing) && (
        <BoatForm
          initial={editing ?? undefined}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['boats'] });
          }}
        />
      )}

      {boats.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {(boats.data ?? []).map((b) => (
            <li key={b.id} className={`p-3 bg-slate-900/30 ${!b.active ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="font-mono text-xs w-16 text-slate-400">{b.sailNumber}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {b.boatName}
                    {b.className && (
                      <span className="text-xs bg-slate-700/40 text-slate-300 px-2 py-0.5 rounded">
                        {b.className}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    Skipper: {b.skipperName}
                    {b.crew && ` • Mannskap: ${b.crew}`}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    Eier:{' '}
                    {b.ownerUsername ? (
                      <span className="text-slate-300">
                        {b.ownerFullName || b.ownerUsername}{' '}
                        <span className="font-mono text-slate-500">({b.ownerUsername})</span>
                      </span>
                    ) : (
                      <span className="italic">ingen</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {!b.active && (
                    <button
                      onClick={() => restore.mutate(b.id)}
                      className="text-xs text-emerald-400"
                    >
                      ↩ Gjenopprett
                    </button>
                  )}
                  <button
                    onClick={() => setOpenPoints(openPoints === b.id ? null : b.id)}
                    className="text-xs text-sky-400"
                    title="Vis alle sporinger for båten (telefon og tracker)"
                  >
                    {openPoints === b.id ? '▾ Sporinger' : '▸ Sporinger'}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(b);
                      setShowNew(false);
                    }}
                    className="text-xs text-sky-400"
                  >
                    Rediger
                  </button>
                </div>
              </div>
              {openPoints === b.id && <TrackHistory source="boat" id={b.id} />}
            </li>
          ))}
          {boats.data?.length === 0 && (
            <li className="p-6 text-center text-slate-500 text-sm">
              Ingen båter enda. Opprett den første.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function BoatForm({ initial, onClose }: { initial?: Boat; onClose: () => void }) {
  const qc = useQueryClient();
  const classes = useQuery({ queryKey: ['classes', false], queryFn: () => api.listClasses(false) });
  const users = useQuery({ queryKey: ['admins'], queryFn: () => api.listAdmins() });
  const trackers = useQuery({ queryKey: ['trackers'], queryFn: () => api.listTrackers() });
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });

  const [form, setForm] = useState({
    sailNumber: initial?.sailNumber ?? '',
    boatName: initial?.boatName ?? '',
    skipperName: initial?.skipperName ?? '',
    crew: initial?.crew ?? '',
    classId: initial?.classId ?? '',
    ownerId: initial?.ownerId ?? '',
    notes: initial?.notes ?? '',
    active: initial?.active ?? true,
    password: initial?.password ?? '',
    skipperEmail: initial?.skipperEmail ?? '',
    skipperMobile: initial?.skipperMobile ?? '',
    trackerId: initial?.trackerId ?? '',
    foreningId: initial?.foreningId ?? '',
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Partial<Boat> = {
        sailNumber: form.sailNumber,
        boatName: form.boatName,
        skipperName: form.skipperName,
        crew: form.crew || null,
        classId: form.classId || null,
        ownerId: form.ownerId || null,
        notes: form.notes || null,
        active: form.active,
        password: form.password || null,
        skipperEmail: form.skipperEmail || null,
        skipperMobile: form.skipperMobile || null,
        trackerId: form.trackerId || null,
        foreningId: form.foreningId || null,
      };
      if (initial) return api.updateBoat(initial.id, payload);
      return api.createBoat(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boats'] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const archive = useMutation({
    mutationFn: async () => initial && api.archiveBoat(initial.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boats'] });
      onClose();
    },
  });

  const restore = useMutation({
    mutationFn: async () => initial && api.updateBoat(initial.id, { active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boats'] });
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">{initial ? 'Rediger båt' : 'Ny båt'}</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Seilnummer</label>
        <input
          value={form.sailNumber ?? ''}
          onChange={(e) => setForm({ ...form, sailNumber: e.target.value })}
          required
          placeholder="f.eks. GJ1234 eller NOR12345"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Båtnavn</label>
        <input
          value={form.boatName ?? ''}
          onChange={(e) => setForm({ ...form, boatName: e.target.value })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Skipper</label>
        <input
          value={form.skipperName ?? ''}
          onChange={(e) => setForm({ ...form, skipperName: e.target.value })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Mannskap (fri tekst)</label>
        <input
          value={form.crew ?? ''}
          onChange={(e) => setForm({ ...form, crew: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          placeholder="Ole, Kari, Per"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Skipper e-post</label>
          <input
            type="email"
            value={form.skipperEmail ?? ''}
            onChange={(e) => setForm({ ...form, skipperEmail: e.target.value })}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
            placeholder="navn@epost.no"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Skipper mobil</label>
          <input
            type="tel"
            value={form.skipperMobile ?? ''}
            onChange={(e) => setForm({ ...form, skipperMobile: e.target.value })}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
            placeholder="f.eks. 412 34 567"
            autoComplete="off"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Båt-passord</label>
        <input
          value={form.password ?? ''}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          autoComplete="off"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
          placeholder="Passord til innlogging / påmelding"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Seileren logger inn på regattaen med båtnummer + dette passordet (påmelding, tidtaking,
          sporing). Tomt = båten kan ikke logge inn selv (kun admin kan melde på).
        </p>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Forening</label>
        <select
          value={form.foreningId ?? ''}
          onChange={(e) => setForm({ ...form, foreningId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Velg forening —</option>
          {(foreninger.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Klasse</label>
        <select
          value={form.classId ?? ''}
          onChange={(e) => setForm({ ...form, classId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen klasse —</option>
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Eier (registrar-bruker)</label>
        <select
          value={form.ownerId ?? ''}
          onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen eier (klubbeid) —</option>
          {(users.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.username} {u.fullName ? `— ${u.fullName}` : ''} {u.role === 'admin' ? '(admin)' : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">GPS-tracker</label>
        <select
          value={form.trackerId ?? ''}
          onChange={(e) => setForm({ ...form, trackerId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen tracker —</option>
          {(trackers.data ?? [])
            .filter((t) => !t.boatId || t.boatId === initial?.id)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.imei}
                {t.label ? ` (${t.label})` : ''}
              </option>
            ))}
        </select>
        <p className="text-[11px] text-slate-500 mt-1">
          Maskinvare-GPS (Teltonika TMT250). Registrer enheter under «Trackere».
        </p>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
      {initial && initial.active && (
        <button
          type="button"
          onClick={() => {
            if (
              confirm(`Arkivere ${initial.boatName}? Båten vil ikke kunne påmeldes nye regattaer.`)
            ) {
              archive.mutate();
            }
          }}
          className="w-full text-xs text-red-400 py-2"
        >
          Arkiver båt
        </button>
      )}
      {initial && !initial.active && (
        <button
          type="button"
          onClick={() => restore.mutate()}
          className="w-full text-xs text-emerald-400 py-2"
        >
          ↩ Gjenopprett båt (angre arkivering)
        </button>
      )}
    </form>
  );
}
